import type { AttributionData } from "@fluid-app/api-client";

import type { FairshareConfig, TrackableEventWithData } from "./core/types";
import { FairShareCore } from "./core/FairShareCore";

// Singleton instance
let instance: FairShareCore | null = null;

/**
 * Get the FairShare instance
 * @returns The FairShare instance
 * @throws Error if FairShare is not initialized
 */
function getInstance(): FairShareCore {
  if (!instance) {
    try {
      instance = FairShareCore.getInstance();
    } catch (error) {
      // Re-throw with a more descriptive message
      console.error("FairShare error:", error);
      throw new Error(
        "FairShare not initialized. Call initializeFairshare first.",
      );
    }
  }
  return instance;
}

/**
 * Initialize the FairShare SDK
 * @param config Configuration options
 * @returns Promise that resolves when initialization is complete
 */
export function initializeFairshare(config: FairshareConfig): Promise<void> {
  // Reset any existing instance to ensure clean initialization
  reset();

  // Create and initialize the instance in one step
  instance = FairShareCore.createInstance(config);

  // Return the initialization Promise from FairShareCore
  return instance.getInitializationPromise();
}

/**
 * Track a custom event
 * @param eventName Event name
 * @param properties Event properties
 * @param options Event options
 * @returns A promise that resolves when the event is tracked
 */
export function trackFairshareEvent(event: TrackableEventWithData): void {
  return getInstance().trackFairshareEvent(event);
}

/**
 * Track a checkout started event specifically using the proper EventManager method
 * This ensures proper metadata building and processing
 * @param data Checkout started event data
 */
export function trackCheckoutStarted(data: { cart_token: string }): void {
  return getInstance().trackCheckoutStarted(data);
}

/**
 * Track a checkout started event synchronously
 * Returns a promise that resolves when the API request completes
 * @param data Checkout started event data
 */
export function trackCheckoutStartedSync(data: {
  cart_token: string;
}): Promise<void> {
  return getInstance().trackCheckoutStartedSync(data);
}

/**
 * Reset the SDK state
 */
export function reset(): void {
  if (instance) {
    instance.reset();
    instance = null;
  }
  // Reset the singleton instance in the core class
  FairShareCore.resetInstance();
}

export function getSessionToken(): string {
  try {
    const fairshareInstance = getInstance();
    const sessionToken = fairshareInstance.getSessionToken();

    if (!sessionToken) {
      // More descriptive error message that explains the possible causes
      throw new Error(
        "Session token not found. This could happen if:\n" +
          "1. initializeFairshare() hasn't been called yet\n" +
          "2. Session initialization is still in progress (wait for the Promise to resolve)\n" +
          "3. Session creation failed (check network connectivity and fluidShop config)\n" +
          "4. Invalid fluidShop identifier provided in configuration",
      );
    }
    return sessionToken;
  } catch (error) {
    // Provide additional debug info based on the error type
    if (
      error instanceof Error &&
      error.message.includes("FairShare not initialized")
    ) {
      console.error(
        "Error: FairShare SDK not initialized. Call initializeFairshare() first.",
      );
    } else {
      console.error("Error retrieving session token:", error);
    }
    throw error;
  }
}

export function getAttribution(): AttributionData | null {
  return getInstance().getAttribution();
}

/**
 * Check if a session token is available without throwing an error
 * @returns true if session token is available, false otherwise
 */
export function hasSessionToken(): boolean {
  try {
    const fairshareInstance = getInstance();
    const sessionToken = fairshareInstance.getSessionToken();
    return !!sessionToken;
  } catch {
    return false;
  }
}

/**
 * Get session token if available, otherwise return null
 * @returns session token string or null if not available
 */
export function getSessionTokenSafe(): string | null {
  try {
    const fairshareInstance = getInstance();
    return fairshareInstance.getSessionToken();
  } catch {
    return null;
  }
}

/**
 * Flush all pending events in the queue
 * @param sync Whether to wait for all events to complete processing
 * @param timeoutMs Maximum time to wait for events to complete (only applies when sync=true)
 * @returns Promise that resolves when events are processed
 */
export function flushEvents(sync = true, timeoutMs = 3000): Promise<void> {
  return getInstance().flushEvents(sync, timeoutMs);
}

/**
 * Flush all pending events using navigator.sendBeacon for reliable delivery during page unload
 * @returns Promise that resolves when events are processed with beacon
 */
export function flushEventsWithBeacon(): Promise<void> {
  return getInstance().flushEventsWithBeacon();
}

// Export types
export type { FairshareConfig };
export type { AttributionData };
export type { TrackableEventWithData };

// Export attribution types
export type { AttributionOverride } from "./core/AttributionManager";
export { AttributionManager } from "./core/AttributionManager";

/**
 * Get the FairShare instance (exported for external use)
 * @returns The FairShare instance
 * @throws Error if FairShare is not initialized
 */
export { getInstance };
